import { useActiveSession } from '@texas/hooks/useSession';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Text,
  Box,
  Spinner,
  Flex,
  Badge,
  Heading,
  Button,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useSubscriber } from '@texas/messageBus/useMessageBus';
import { OpenGlobalSearch } from '@texas/messageBus/messages';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  searchApi,
  GlobalSearchResponse,
} from '@texas/api/endpoints/search/searchApi';
import { ArticleItem } from './ArticleItem';
import { ProductApprovalItem } from './ProductApprovalItem';
import { CustomerItem } from './CustomerItem';
import { ConceptItem } from './ConceptItem';
import { SupplierItem } from './SupplierItem';
import { InquiryItem } from './InquiryItem';
import { RecentEntities } from './RecentEntities';
import { TFunction } from 'i18next';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { useNavigationStore } from '@texas/components/navigation/hooks/useNavigationStore';
import { Icons } from '@texas/components/shared/Icons';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { PortalRefProvider, usePortalRef } from '@texas/hooks/usePortalRef';

type EntityKey = keyof GlobalSearchResponse;

const entityOrder: EntityKey[] = [
  'articles',
  'productApprovals',
  'customers',
  'concepts',
  'suppliers',
  'inquiries',
];

export function GlobalSearchModal() {
  const { t } = useTranslation();
  const {
    currentUser: { betaFeatures },
  } = useActiveSession();
  const visibleEntityOrder = useMemo(
    () => entityOrder.filter((key) => betaFeatures || key !== 'inquiries'),
    [betaFeatures],
  );
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getCustomer } = useNavigationStore();
  const customer = getCustomer();
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );
  const customerId =
    selectedCustomerId === customer?.id ? selectedCustomerId : null;
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<EntityKey>>(new Set());
  const [includeArchived, setIncludeArchived] = useState(false);
  const portalRef = usePortalRef();
  const {
    data: results,
    refetch,
    loading,
    error,
    set,
  } = useApiResource(searchApi.globalSearch);

  useSubscriber(OpenGlobalSearch, (message) => {
    setSelectedCustomerId(
      message.customerId != null && message.customerId === customer?.id
        ? message.customerId
        : null,
    );
    setSearchTerm('');
    setActiveFilters(new Set());
    set(null);
    onOpen();
  });

  useEffect(() => {
    setActiveFilters(
      (previous) =>
        new Set(
          [...previous].filter((key) => visibleEntityOrder.includes(key)),
        ),
    );
  }, [visibleEntityOrder]);

  useEffect(() => {
    set(null);
    if (isOpen && searchTerm.trim().length >= 2) {
      refetch({
        searchTerm,
        resultsPerEntity: 10,
        includeArchived,
        customerId,
      });
    } else {
      set(null);
      setActiveFilters(new Set());
    }
  }, [isOpen, searchTerm, includeArchived, customerId, refetch, set]);

  useEffect(() => {
    if (!results) {
      return;
    } else if (activeFilters.size > 0) {
      return;
    }

    const withResults = visibleEntityOrder.filter(
      (key) => results[key].length > 0,
    );
    setActiveFilters((prev) => {
      const next = new Set(prev);
      withResults.forEach((key) => {
        if (!next.has(key)) next.add(key);
      });
      return next;
    });
  }, [activeFilters.size, results, visibleEntityOrder]);

  const toggleFilter = (key: EntityKey) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const changeScope = (id: number | null) => {
    setSelectedCustomerId(id);
    setActiveFilters(new Set());
    set(null);
  };

  const handleResultClick = () => {
    setSearchTerm('');
    onClose();
  };

  const hasResults =
    results && visibleEntityOrder.some((key) => results[key].length > 0);

  const noResults =
    results && searchTerm.length >= 2 && !loading && !hasResults;

  // Entity keys that have at least one result (drives which toggle buttons are shown)
  const entitiesWithResults = results
    ? visibleEntityOrder.filter((key) => results[key].length > 0)
    : [];

  return (
    <PortalRefProvider value={portalRef}>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex alignItems="center" gap={2}>
              {t('general.search')}
              {loading && <Spinner size="sm" />}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} {...portalRef.bindRef}>
            <Flex
              align="stretch"
              gap={2}
              direction={{ base: 'column', sm: 'row' }}
            >
              <Menu>
                <MenuButton
                  as={Button}
                  variant="entityDirectorySecondary"
                  rightIcon={<Icons.ChevronDown />}
                  aria-label={t('globalSearch.scope')}
                  maxW={{ base: 'full', sm: '240px' }}
                  minW="120px"
                  flexShrink={0}
                  h="42px"
                >
                  <Text noOfLines={1}>
                    {customerId ? customer?.name : t('globalSearch.global')}
                  </Text>
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => changeScope(null)}>
                    {t('globalSearch.global')}
                  </MenuItem>
                  {customer && (
                    <MenuItem onClick={() => changeScope(customer.id)}>
                      {customer.name}
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
              <Box flex={1} minW={0}>
                <SearchFilterInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  autoFocus={true}
                  style="border"
                />
              </Box>
            </Flex>
            {searchTerm === '' && (
              <RecentEntities
                key={customerId ?? 'global'}
                customerId={customerId}
                onNavigate={handleResultClick}
              />
            )}
            {error && <ErrorDetails error={error} />}
            <Flex mt={3} alignItems="center" gap={2} flexWrap="wrap">
              {searchTerm !== '' && (
                <TexasSwitch
                  checked={includeArchived}
                  onChange={setIncludeArchived}
                  label={t('general.includeArchived')}
                />
              )}
              {hasResults && entitiesWithResults.length > 0 && (
                <ButtonGroup size="sm" flexWrap="wrap" gap={0} display="flex">
                  {entitiesWithResults.map((key) => (
                    <Button
                      key={key}
                      variant={
                        activeFilters.has(key) ? 'texas-solid' : 'outline'
                      }
                      onClick={() => toggleFilter(key)}
                      isDisabled={
                        activeFilters.has(key) && activeFilters.size === 1
                      }
                      size="sm"
                    >
                      {entityLabel(key, t)}
                      <Badge
                        colorScheme="gray"
                        color="white"
                        _light={{ color: 'black' }}
                        ml={2}
                      >
                        {results[key].length}
                      </Badge>
                    </Button>
                  ))}
                </ButtonGroup>
              )}
            </Flex>

            {noResults && (
              <Box py={8} textAlign="center">
                <Text color="gray.400">{t('general.noResult')}</Text>
              </Box>
            )}

            {results && hasResults && (
              <VStack mt={4} align="stretch" spacing={0}>
                {visibleEntityOrder.map((key) => {
                  if (!activeFilters.has(key) || results[key].length === 0)
                    return null;

                  return (
                    <Box key={key} mb={4}>
                      <Flex
                        alignItems="center"
                        mb={2}
                        bg="whiteAlpha.100"
                        p={2}
                        borderRadius="md"
                        _light={{ bg: 'blackAlpha.100' }}
                      >
                        <Heading
                          fontSize="sm"
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          {entityLabel(key, t)}
                        </Heading>
                        <Badge ml={2}>{results[key].length}</Badge>
                      </Flex>

                      <VStack align="stretch" spacing={0}>
                        {key === 'articles' &&
                          results.articles.map((article) => (
                            <ArticleItem
                              key={article.id}
                              article={article}
                              onResultClick={handleResultClick}
                            />
                          ))}

                        {key === 'productApprovals' &&
                          results.productApprovals.map((pa) => (
                            <ProductApprovalItem
                              key={pa.id}
                              productApproval={pa}
                              onResultClick={handleResultClick}
                            />
                          ))}

                        {key === 'customers' &&
                          results.customers.map((customer) => (
                            <CustomerItem
                              key={customer.id}
                              customer={customer}
                              onResultClick={handleResultClick}
                            />
                          ))}

                        {key === 'concepts' &&
                          results.concepts.map((concept) => (
                            <ConceptItem
                              key={concept.id}
                              concept={concept}
                              onResultClick={handleResultClick}
                            />
                          ))}

                        {key === 'suppliers' &&
                          results.suppliers.map((supplier) => (
                            <SupplierItem
                              key={supplier.id}
                              supplier={supplier}
                              onResultClick={handleResultClick}
                            />
                          ))}

                        {key === 'inquiries' &&
                          results.inquiries.map((inquiry) => (
                            <InquiryItem
                              key={inquiry.id}
                              inquiry={inquiry}
                              onResultClick={handleResultClick}
                            />
                          ))}
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PortalRefProvider>
  );
}

const entityLabel = (key: EntityKey, t: TFunction): string => {
  switch (key) {
    case 'articles':
      return t('general.articles');
    case 'productApprovals':
      return t('productApproval.productApprovals');
    case 'customers':
      return t('general.customers');
    case 'concepts':
      return t('general.concepts');
    case 'suppliers':
      return t('general.suppliers');
    case 'inquiries':
      return t('inquiry.inquiries');
  }
};
